import { defaults, values } from 'lodash-es';
import * as yup from 'yup';
import { CampaignType } from '../../constants/postType.constants';

const GivingOptionUnitType = {
  metric: [
    { value: 'numbers', label: 'Nos' },
    { value: 'kg', label: 'Kgs' },
    { value: 'liters', label: 'Liters' },
    { value: 'meters', label: 'Meters' },
  ],
  imperial: [
    { value: 'numbers', label: 'Nos' },
    { value: 'lbs', label: 'Lbs' },
    { value: 'gallons', label: 'Gallons' },
    { value: 'feet', label: 'Feet' },
  ],
};

const inKindGoodSchema = yup.object().shape({
  pricePerUnit: yup
    .number()
    .required('Approximate unit price is required')
    // .test(
    //   'is-not-greater-than-0',
    //   'Approximate unit price must be greater than 0',
    //   (value) => value <= 0,
    // )
    .typeError('Approximate unit price must be valid and greater than 0')
    .when('$campaignType', {
      is: CampaignType.CollectGoods,
      then: yup
        .number()
        .required('Approximate unit price is required')
        .min(0.0000001, 'Approximate unit price must be greater than 0')
        .test(
          'is-not-nan',
          'Approximate unit price must valid and greater than 0',
          (value) => !isNaN(value),
        )
        .typeError('Approximate unit price must be greater than 0'),
      // otherwise: yup.number().notRequired().nullable(),
    }),
  unit: yup
    .string()
    .required('Unit is required. Please select a unit')
    .typeError('Unit is required. Please select a unit')
    .when('$campaignType', {
      is: CampaignType.CollectGoods,
      then: yup
        .string()
        .required('Unit is required. Please select a unit')
        .test(
          'is-not-null',
          'Unit is required. Please select a unit',
          (value) => value !== null,
        )
        .typeError('Unit is required. Please select a unit'),
      // otherwise: yup.string().notRequired().nullable(),
    }),
});

const inKindGoodArraySchema = yup.array().of(inKindGoodSchema);

const projectSchema = yup.object().shape({
  name: yup.string().required('Name is required').max(110, 'Name is too long'),
  description: yup.string().required('Description is required'),
  campaignType: yup
    .string()
    .oneOf(values(CampaignType))
    .required('Campaign type is required'),
  address: yup.string().when('campaignType', {
    is: CampaignType.CollectGoods,
    then: yup.string().required('Address is required'),
    otherwise: yup.string().notRequired().nullable(),
  }),
  givingLevels: yup
    .array()
    .of(
      yup.object().shape({
        quantity: yup
          .number()
          .required('Quantity is required')
          .min(0.000001, 'Quantity must be greater than 0')
          .typeError('Quantity must be greater than 0'),
        title: yup
          .string()
          .required('Title is required')
          .max(100, 'Title is too long'),
        description: yup
          .string()
          .required('Description is required')
          .max(500, 'Description is too long'),
        // pricePerUnit: yup
        //   .number()
        //   // .required('Approximate unit price is required')
        //   // .test(
        //   //   'is-not-greater-than-0',
        //   //   'Approximate unit price must be greater than 0',
        //   //   (value) => value <= 0,
        //   // )
        //   // .typeError('Approximate unit price must valid and greater than 0')
        //   .when('$campaignType', {
        //     is: CampaignType.CollectGoods,
        //     then: yup
        //       .number()
        //       .required('Approximate unit price is required')
        //       .min(0.0000001, 'Approximate unit price must be greater than 0')
        //       .test(
        //         'is-not-nan',
        //         'Approximate unit price must valid and greater than 0',
        //         (value) => !isNaN(value),
        //       )
        //       .typeError('Approximate unit price must be greater than 0'),
        //     // otherwise: yup.number().notRequired().nullable(),
        //   }),
        // unit: yup
        //   .string()
        //   // .required('Unit is required. Please select a unit')
        //   // .typeError('Unit is required. Please select a unit')
        //   .when('$campaignType', {
        //     is: CampaignType.CollectGoods,
        //     then: yup
        //       .string()
        //       .required('Unit is required. Please select a unit')
        //       .test(
        //         'is-not-null',
        //         'Unit is required. Please select a unit',
        //         (value) => value !== null,
        //       )
        //       .typeError('Unit is required. Please select a unit'),
        //     // otherwise: yup.string().notRequired().nullable(),
        //   }),
      }),
    )
    .when('campaignType', {
      is: CampaignType.CollectGoods, // Add this condition for "In Kind" campaign type
      then: yup.array().concat(inKindGoodArraySchema),
      otherwise: yup.array(),
    }),
  status: yup.string().oneOf(['active', 'inactive', 'deleted']),
});

const defaultProjectFormValues = (project) => {
  const {
    postAs,
    targetViewers,
    targetOrgs,
    isFeatured,
    name,
    description,
    campaignType,
    address,
    givingLevels,
    status,
  } = project || {
    postAs: 'org',
    targetViewers: 'all',
    targetOrgs: [],
    isFeatured: false,
    name: '',
    description: '',
    campaignType: CampaignType.FundRaise,
    address: '',
    givingLevels: [
      {
        title: '',
        quantity: null,
        description: '',
        pricePerUnit: CampaignType.CollectGoods ? 1 : null,
        unit: null,
      },
    ],
    status: 'active',
  };

  return {
    postAs: postAs,
    targetViewers: targetViewers,
    targetOrgs: targetOrgs,
    isFeatured: isFeatured,
    name: name,
    description: description,
    campaignType: campaignType,
    address: address,
    givingLevels: givingLevels.map((givingLevel) =>
      defaults({}, givingLevel, {
        title: '',
        quantity: null,
        description: '',
        pricePerUnit: CampaignType.CollectGoods ? 1 : null,
        unit: null,
      }),
    ),
    status: status,
  };
};

const getGivingOptionUnitsForLocale = (country) => {
  if (country === 'US') {
    return GivingOptionUnitType.imperial;
  }
  return GivingOptionUnitType.metric;
};

export {
  projectSchema,
  defaultProjectFormValues,
  getGivingOptionUnitsForLocale,
};
